import { renderRowsWithSpacing } from "@/lib/accordion-content";

type MultiplesampleHistoryItem = {
  id?: string;
  id_imported?: number;
  provenanceRank?: number;
  exemplaireDansEstampesMultiplesHistoryDescriptionCollectionPart?: boolean;
  exemplaireDansEstampesMultiplesHistoryDescriptionNom?: string;
  exemplaireDansEstampesMultiplesHistoryDescriptionVille?: string;
  exemplaireDansEstampesMultiplesHistoryDescriptionDates?: string;
};

type MultiplesampleAuctionItem = {
  id?: string;
  provenanceRank?: number;
  auction?: {
    ventesDescriptionNumeroVente?: string;
    ventesDescriptionTitre?: string;
    ventesDescriptionDate?: string;
    ventesDescriptionCommissaires?: string;
    ventesDescriptionLieu?: string;
    ventesDescriptionVille?: string;
    ventesDescriptionPays?: string;
  };
  multiplesampleDansVentesDescriptionNumeroLot?: string;
};

type ProvenanceRow =
  | {
      kind: "history";
      index: number;
      item: MultiplesampleHistoryItem;
    }
  | {
      kind: "auction";
      index: number;
      item: MultiplesampleAuctionItem;
    };

const cleanParts = (parts: Array<string | null | undefined>) =>
  parts.map((part) => part?.trim()).filter(Boolean) as string[];

const hasPersistedOrder = (rows: ProvenanceRow[]) =>
  rows.some((row) => (row.item.provenanceRank ?? 0) > 0);

const getFallbackOrder = (row: ProvenanceRow, historyCount: number) => {
  if (row.kind === "history") return row.item.id_imported ?? row.index;
  return historyCount + row.index;
};

export function buildMultiplesampleProvenanceHistory({
  history,
  auctions,
  t,
}: {
  history?: MultiplesampleHistoryItem[];
  auctions?: MultiplesampleAuctionItem[];
  t: (key: string) => string;
}) {
  const historyRows: ProvenanceRow[] = (history ?? []).map((item, index) => ({
    kind: "history",
    item,
    index,
  }));
  const auctionRows: ProvenanceRow[] = (auctions ?? []).map((item, index) => ({
    kind: "auction",
    item,
    index,
  }));
  const rows = [...historyRows, ...auctionRows];
  if (rows.length === 0) return null;

  const shouldUsePersistedOrder = hasPersistedOrder(rows);
  rows.sort((a, b) => {
    const aRank = shouldUsePersistedOrder
      ? (a.item.provenanceRank ?? 0)
      : getFallbackOrder(a, historyRows.length);
    const bRank = shouldUsePersistedOrder
      ? (b.item.provenanceRank ?? 0)
      : getFallbackOrder(b, historyRows.length);
    if (aRank !== bRank) return aRank - bRank;
    if (a.kind !== b.kind) return a.kind === "history" ? -1 : 1;
    return (a.item.id ?? "").localeCompare(b.item.id ?? "");
  });

  const renderedRows = rows
    .map((row) => {
      if (row.kind === "history") {
        const item = row.item;
        const location = item.exemplaireDansEstampesMultiplesHistoryDescriptionCollectionPart
          ? t("computeField.collectionParticuliere")
          : item.exemplaireDansEstampesMultiplesHistoryDescriptionNom;
        return cleanParts([
          location,
          item.exemplaireDansEstampesMultiplesHistoryDescriptionVille,
          item.exemplaireDansEstampesMultiplesHistoryDescriptionDates,
        ]).join(", ");
      }

      const item = row.item;
      const location = cleanParts([
        item.auction?.ventesDescriptionCommissaires,
        item.auction?.ventesDescriptionLieu,
        item.auction?.ventesDescriptionVille,
        item.auction?.ventesDescriptionPays,
        item.multiplesampleDansVentesDescriptionNumeroLot
          ? `lot n°${item.multiplesampleDansVentesDescriptionNumeroLot}`
          : null,
      ]).join(", ");
      return cleanParts([
        item.auction?.ventesDescriptionDate,
        location,
      ]).join(" - ");
    })
    .filter(Boolean);

  return renderedRows.length > 0 ? renderRowsWithSpacing(renderedRows) : null;
}
